import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchSlackNotificationConfig,
  updateSlackNotificationConfig,
  testSlackNotificationConfig,
} from '../../utils/api/app_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { SLACK_CHANNEL_CONFIG_COPY } from '../notifications/notification.constants';
import ButtonComponent from '../customButton';
import InputBox from '../FormComponent/InputBox';
import CheckboxComponent from '../FormComponent/CheckboxComponent';
import PILComponent from '../common/PILComponent';
import TextSkeletonContainer from '../Skeleton/TextSkeletonContainer';
import styles from './ProjectNotificationSettings.module.scss';

const SLACK_EVENT_KEYS = Object.keys(SLACK_CHANNEL_CONFIG_COPY.events);

const getErrorMessage = (response, fallback) => response?.error || fallback;

const SlackChannelConfigTab = ({ projectId }) => {
  const { showSnackbar } = useSnackbar();
  const [connected, setConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [active, setActive] = useState(false);
  const [channelId, setChannelId] = useState('');
  const [events, setEvents] = useState([]);
  const [channelError, setChannelError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError('');

    const response = await fetchSlackNotificationConfig(projectId);

    if (response.status !== STATUS.SUCCESS) {
      setLoadError(getErrorMessage(response, SLACK_CHANNEL_CONFIG_COPY.loadError));
      setConnected(false);
      setIsLoading(false);
      return;
    }

    const data = response.data || {};
    setConnected(Boolean(data.connected));
    setActive(Boolean(data.active));
    setChannelId(data.channelId || '');
    setEvents(Array.isArray(data.events) ? data.events : []);
    setIsLoading(false);
  }, [projectId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const toggleEvent = (eventKey, checked) => {
    setEvents((prev) => {
      if (checked) return prev.includes(eventKey) ? prev : [...prev, eventKey];
      return prev.filter((key) => key !== eventKey);
    });
  };

  const validateChannel = () => {
    if (!channelId.trim()) {
      setChannelError(SLACK_CHANNEL_CONFIG_COPY.channelRequired);
      return false;
    }
    setChannelError('');
    return true;
  };

  const handleTest = async () => {
    if (!validateChannel()) return;

    setIsTesting(true);
    const response = await testSlackNotificationConfig(projectId, channelId.trim());
    setIsTesting(false);

    if (response.status !== STATUS.SUCCESS) {
      showSnackbar(getErrorMessage(response, SLACK_CHANNEL_CONFIG_COPY.testError), 'error');
      return;
    }

    showSnackbar(SLACK_CHANNEL_CONFIG_COPY.testSuccess, 'success');
  };

  const handleSave = async () => {
    if (events.length > 0 && !validateChannel()) return;

    setIsSaving(true);
    const response = await updateSlackNotificationConfig(projectId, {
      active,
      channelId: channelId.trim() || null,
      events,
    });
    setIsSaving(false);

    if (response.status !== STATUS.SUCCESS) {
      showSnackbar(getErrorMessage(response, SLACK_CHANNEL_CONFIG_COPY.saveError), 'error');
      return;
    }

    // The server only persists (and echoes back) a channel once at least one
    // event is selected - `channelId: null` here means "not stored yet," not
    // "rejected." Keep whatever the admin typed locally instead of blanking
    // the field, so checking an event and saving again doesn't feel like the
    // channel they just entered vanished.
    const data = response.data || {};
    setActive(Boolean(data.active));
    if (data.channelId) setChannelId(data.channelId);
    setEvents(Array.isArray(data.events) ? data.events : []);
    showSnackbar(SLACK_CHANNEL_CONFIG_COPY.saveSuccess, 'success');
  };

  if (isLoading) {
    return (
      <div className={styles.config}>
        <TextSkeletonContainer width="100%" height={20} lines={2} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.config}>
        <div className={styles.emptyState}>
          <p>{loadError}</p>
        </div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className={styles.config}>
        <div className={styles.emptyState}>
          <h3>{SLACK_CHANNEL_CONFIG_COPY.emptyTitle}</h3>
          <p>{SLACK_CHANNEL_CONFIG_COPY.emptyDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <section className={styles.config} aria-labelledby="slack-config-heading">
      <div className={styles.headerRow}>
        <header className={styles.header}>
          <h2 id="slack-config-heading">{SLACK_CHANNEL_CONFIG_COPY.heading}</h2>
          <p>{SLACK_CHANNEL_CONFIG_COPY.description}</p>
        </header>
        <button
          type="button"
          role="switch"
          aria-checked={active}
          className={styles.activeToggle}
          onClick={() => setActive((prev) => !prev)}
          disabled={isSaving}
        >
          <PILComponent
            label={active ? SLACK_CHANNEL_CONFIG_COPY.activeOnLabel : SLACK_CHANNEL_CONFIG_COPY.activeOffLabel}
            type={active ? 'success' : 'disabled'}
            size="medium"
          />
        </button>
      </div>

      <div className={styles.form}>
        <InputBox
          id="slack-channel-id"
          name="channelId"
          label={SLACK_CHANNEL_CONFIG_COPY.channelLabel}
          value={channelId}
          onChange={(event) => {
            setChannelId(event.target.value);
            if (channelError) setChannelError('');
          }}
          placeholder={SLACK_CHANNEL_CONFIG_COPY.channelPlaceholder}
          disabled={isSaving || isTesting}
          maxLength={50}
          error={channelError || undefined}
        />
        {events.length === 0 && !channelError && (
          <p className={styles.channelHint}>{SLACK_CHANNEL_CONFIG_COPY.channelHint}</p>
        )}

        <div className={styles.formRow}>
          <span className={styles.formRowLabel}>{SLACK_CHANNEL_CONFIG_COPY.eventsLabel}</span>
          <div className={styles.checkboxList}>
            {SLACK_EVENT_KEYS.map((eventKey) => (
              <CheckboxComponent
                key={eventKey}
                label={SLACK_CHANNEL_CONFIG_COPY.events[eventKey]}
                checked={events.includes(eventKey)}
                onChange={(event) => toggleEvent(eventKey, event.target.checked)}
                disabled={isSaving}
              />
            ))}
          </div>
        </div>

        <div className={styles.actions}>
          <ButtonComponent
            ctaText={SLACK_CHANNEL_CONFIG_COPY.save}
            variant="primary"
            className="white bg-blue600"
            onClick={handleSave}
            disabled={isTesting}
            loading={isSaving}
          />
          <ButtonComponent
            ctaText={SLACK_CHANNEL_CONFIG_COPY.test}
            variant="secondary"
            className="slate900 bg-slate200"
            onClick={handleTest}
            disabled={isSaving}
            loading={isTesting}
          />
        </div>
      </div>
    </section>
  );
};

export default SlackChannelConfigTab;
